import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  OnInit,
  Renderer2,
  ViewChild
} from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, debounceTime, map, switchMap, takeUntil } from 'rxjs/operators';

import { ApiUrl }       from '../../../../shared/models/apiurl.models';
import { LoadingMaskServiceService } from '../../../../components/loading-frame/loading-frame.component';
import { SystemApp }    from '../../../../constans/SystemConfigurations';
import { Product, EntityType } from '../../../../models/product';
import { AppService }   from '../../../../app.service';
import { BusinessService } from '../../../../services/business.service';
import { InquiryService } from '../../../../services/inquiry.service';
import { Router } from '@angular/router';
import { FilterbaseService } from '../../top-filter/filter-base/services/filterbase.service';
import {ListingStateService} from "./listing.state.service";
import {NadlanMapService} from "../../../../components/nadlan-map/nadlan-map.service";

@Component({
    selector: 'app-products-container',
    templateUrl: './products-container.component.html',
    styleUrls: ['./products-container.component.css'],
    standalone: false
})
export class ProductsContainerComponent implements OnInit, OnChanges, OnDestroy {

  /** Auto-derived from the current URL — no need to pass it manually */
  stateKey: string;
  @Input() productsList: Product[] = [];
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;
  /** SCRUM-253: the list as shown (the real-estate map follows it). */
  @Output() productsChange = new EventEmitter<any[]>();

  isLoading     = false;
  isLoadingMore = false;
  totalPages    = 0;

  private readonly fetchTrigger$ = new Subject<void>();
  private readonly destroy$      = new Subject<void>();

  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type':  'application/json',
      'Authorization': this.apiKey.getJWTToken()
    })
  };

  constructor(
      private readonly http:        HttpClient,
      private readonly apiKey:      ApiUrl,
      private readonly loadingMask: LoadingMaskServiceService,
      public  readonly appService:  AppService,
      private readonly renderer:    Renderer2,
      private readonly router:        Router,
      public  readonly filterbaseService: FilterbaseService,
      private readonly listingState:  ListingStateService,
      public  readonly mapService:    NadlanMapService,
      private readonly inquiries:     InquiryService
  ) {
    // Use the full URL (path + query string) as the cache key so
    // /cars/private and /cars/commercial each get their own state
    this.stateKey = this.router.url;
    this.initFetchPipeline();
  }

  /* ─── Lifecycle ──────────────────────────────────────────────── */

  ngOnInit(): void {
    const saved = this.listingState.restore(this.stateKey);

    if (saved) {
      // Restore cached list + pagination state
      this.productsList             = saved.products;
      this.totalPages               = saved.totalPages;
      this.filterbaseService.offset = saved.offset;
      setTimeout(() => this.productsChange.emit(this.productsList));
      // Scroll back to saved position after Angular renders the list
      setTimeout(() => window.scrollTo({ top: saved.scrollY,  }), 0);
    } else {
      this.loadingMask.setOnLoadingMask(true, true);
      this.fetchTrigger$.next();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['productsList'] && !changes['productsList'].firstChange) {
      // A reload from outside (filters applied, map area) starts at page 1
      // with a new page count - the old count stopped infinite scroll early or
      // let it ask for pages that do not exist.
      this.totalPages = this.filterbaseService.lastTotalPages;
      this.productsChange.emit(this.productsList);
    }
  }

  /** SCRUM-253: list ↔ map hover sync (only while a map is shown). */
  onCardEnter(item: any): void { if (this.mapService.active) this.mapService.hover(item?.id ?? null, 'list'); }
  onCardLeave(): void { if (this.mapService.active) this.mapService.hover(null, 'list'); }
  showOnMap(event: Event, item: any): void {
    event.preventDefault();
    event.stopPropagation();
    this.mapService.focus(item.id);
  }
  hasPosition(item: any): boolean {
    return item?.Street?.lat != null || item?.City?.lat != null;
  }

  ngAfterViewInit(): void {
    // scroll is detected via @HostListener on window — nothing to set up here
  }

  ngOnDestroy(): void {
    // Persist current list + scroll position for back navigation
    this.listingState.save(this.stateKey, {
      products:   this.productsList,
      totalPages: this.totalPages,
      offset:     this.filterbaseService.offset,
      scrollY:    window.scrollY,
    });
    this.destroy$.next();
    this.destroy$.complete();
  }

  /* ─── Pagination ─────────────────────────────────────────────── */

  get isFirstPage(): boolean { return this.filterbaseService.offset <= 1; }
  get isLastPage():  boolean { return this.filterbaseService.offset >= this.totalPages; }

  range(total: number): number[] {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  previousPage(): void { if (!this.isFirstPage) this.goToPage(this.filterbaseService.offset - 1); }
  nextPage():     void { if (!this.isLastPage)  this.goToPage(this.filterbaseService.offset + 1); }
  pageChange(p: number): void { this.goToPage(p); }

  /* ─── Wishlist ───────────────────────────────────────────────── */

  addToWishList(event: Event, product: Product): void {
    event.preventDefault();
    this.appService.addToWishList(product);
  }

  /* ═══════════════════════════════════════════════════════════════
     TEMPLATE HELPERS  (model-aware)
     ═══════════════════════════════════════════════════════════════ */

  /** Icon emoji per resourcetype */
  getCategoryIcon(resourcetype: string | null | undefined): string {
    const map: Record<string, string> = {
      [EntityType.CAR]:      '🚗',
      [EntityType.NADLAN]:   '🏠',
      [EntityType.SALE2]:    '🛍️',
      [EntityType.CELLULAR]: '📱',
      [EntityType.FURNITURE]:'🛋️',
      [EntityType.FASHION]:  '👗',
      [EntityType.BOYBABY]:  '🧸',
      [EntityType.PET]:      '🐾',
      [EntityType.COWORKER]: '💼',
      [EntityType.REQUIRED]: '📋',
    };
    return map[resourcetype ?? ''] ?? '📦';
  }

  /** Human-readable category label */
  getCategoryLabel(item: Product): string {
    const labelMap: Partial<Record<string, string>> = {
      [EntityType.CAR]:      item.sconderyCategory?.name ?? 'רכב',
      [EntityType.NADLAN]:   item.sconderyCategory?.name ?? 'נדל"ן',
      [EntityType.SALE2]:    item.sconderyCategory?.name ?? 'יד שניה',
      [EntityType.CELLULAR]: 'סלולר',
      [EntityType.FURNITURE]:'ריהוט',
      [EntityType.FASHION]:  'אופנה',
      [EntityType.BOYBABY]:  'תינוקות',
      [EntityType.PET]:      'חיות מחמד',
      [EntityType.COWORKER]: item.professionCategory?.label ?? 'בעלי מקצוע',
      [EntityType.REQUIRED]: item.jobCategory?.label ?? 'דרושים',
    };
    return labelMap[item.resourcetype ?? ''] ?? item.mainCategory?.name ?? '';
  }

  /**
   * Smart title per type:
   *  - Car      → "טויוטה קורולה 2016"  (CodeProducer + MainModel may be
   *                strings OR objects depending on API version)
   *  - Nadlan   → address from Street / City
   *  - Coworker → businessName or professionalTitle
   *  - Required → companyName or jobTitle
   *  - Pet      → breed + petName
   *  - Others   → item.name
   */

  /** SCRUM-262 — "מתווך" / "סוחר רכב" / "עסק" for business advertisers, else null. */
  businessTag(item: any): string | null {
    return BusinessService.tagFor(item);
  }

  /** The seller's (optional) business profile badge: name, logo, verified. */
  businessStore(item: any): { name?: string; logo?: string; verified?: boolean; verified_text?: string } | null {
    return item?.User?.store || null;
  }

  /** Every real listing gets the seller row; site (internal) ads do not. */
  showSellerRow(item: any): boolean {
    return String(item?.resourcetype || '').toLowerCase() !== 'internalads' && !!this.sellerId(item);
  }

  /** Dealer row: business name, else the seller's own name. */
  dealerName(item: any): string {
    const u = item?.User || {};
    return item?.User?.store?.name
      || [u.first_name, u.last_name].filter(Boolean).join(' ').trim()
      || item?.userContactDetails?.contactPerson
      || (this.businessTag(item) ? 'עסק' : 'מוכר פרטי');
  }

  /** Dealer row: business logo, else the seller's avatar, else null (initials). */
  dealerAvatar(item: any): string | null {
    return item?.User?.store?.logo || item?.User?.avatar || item?.User?.profilePhoto || null;
  }

  dealerInitials(item: any): string {
    const parts = this.dealerName(item).trim().split(/\s+/).filter(Boolean);
    return ((parts[0]?.charAt(0) || '') + (parts[1]?.charAt(0) || '')).toUpperCase() || '?';
  }

  /** Icon for the advertiser badge, by the listing's category. */
  tagIcon(item: any): string {
    switch (String(item?.resourcetype || '').toLowerCase()) {
      case 'car':    return 'mdi-car';
      case 'nadlan': return 'mdi-home';
      default:       return 'mdi-store';
    }
  }

  /** SCRUM-266 — a paid placement (promotion or paid route), labelled "ממומן". */
  isSponsored(item: any): boolean {
    return item?.sponsored === true;
  }

  sellerId(item: any): number | null {
    return item?.User?.id ?? null;
  }

  /** Seller row → write to the seller without leaving the page (WhatsApp relay). */
  messageSeller(event: Event, item: any): void {
    event.preventDefault();
    event.stopPropagation();
    this.inquiries.open({
      adId: item.id,
      adName: this.getTitle(item),
      phone: item?.userContactDetails?.phoneNumber || '',
    });
  }

  getTitle(item: Product): string {
    switch (item.resourcetype) {
      case EntityType.CAR: {
        const producer = typeof item.CodeProducer === 'string'
            ? item.CodeProducer
            : (item.CodeProducer as any)?.producer ?? '';
        const model = typeof item.MainModel === 'string'
            ? item.MainModel
            : (item.MainModel as any)?.model ?? '';
        const year  = item.year ?? '';
        return [producer, model, year].filter(Boolean).join(' ') || item.name;
      }
      case EntityType.COWORKER:
        return (!item.isAnonymous && item.businessName)
            ? item.businessName
            : (item.professionalTitle ?? item.name);
      case EntityType.REQUIRED:
        return (!item.isAnonymous && item.companyName)
            ? item.companyName
            : (item.jobTitle ?? item.name);
      case EntityType.PET:
        return item.breed?.label
            ? `${item.breed.label}${item.petName ? ' · ' + item.petName : ''}`
            : item.name;
      default:
        return item.name;
    }
  }

  /** Price label differs by type */
  getPriceLabel(item: Product): string {
    switch (item.resourcetype) {
      case EntityType.COWORKER: return `שכר ${item.pricingType?.label ?? 'שעתי'}`;
      case EntityType.REQUIRED: return `שכר ${item.salaryType?.label ?? 'חודשי'}`;
      case EntityType.PET:      return item.freeAdoption ? 'אימוץ' : 'מחיר';
      case EntityType.NADLAN:
        return item.sconderyCategory?.subCategory === 'RENTAL' ? 'שכירות חודשית' : 'מחיר';
      default: return 'מחיר';
    }
  }

  /** Formatted price / range string */
  getDisplayPrice(item: Product): string {
    if (item.freeAdoption) return 'חינם לאימוץ';

    if (item.resourcetype === EntityType.COWORKER) {
      const from = item.priceFrom ? Math.round(+item.priceFrom) : null;
      const to   = item.priceTo   ? Math.round(+item.priceTo)   : null;
      if (from && to) return `₪${from}–₪${to}`;
      if (from)       return `מ-₪${from}`;
    }

    if (item.resourcetype === EntityType.REQUIRED) {
      const from = item.salaryFrom ? Math.round(+item.salaryFrom) : null;
      const to   = item.salaryTo   ? Math.round(+item.salaryTo)   : null;
      if (from && to) return `₪${from}–₪${to}`;
      if (from)       return `מ-₪${from}`;
    }

    return item.price != null ? `₪${item.price.toLocaleString('he-IL')}` : '—';
  }

  /**
   * Returns true for all Sale2-family types that share the same
   * template block (Sale2, CELLULAR, FURNITURE, FASHION, BOYBABY).
   */
  isSale2Type(resourcetype: string | null | undefined): boolean {
    return [
      EntityType.SALE2,
      EntityType.CELLULAR,
      EntityType.FURNITURE,
      EntityType.FASHION,
      EntityType.BOYBABY,
    ].includes(resourcetype as EntityType);
  }

  /** Called from the template when the user taps a card — saves before Angular destroys the component */
  saveState(): void {
    this.listingState.save(this.stateKey, {
      products:   this.productsList,
      totalPages: this.totalPages,
      offset:     this.filterbaseService.offset,
      scrollY:    window.scrollY,
    });
  }

  /* ─── Infinite Scroll ────────────────────────────────────────── */

  @HostListener('window:scroll')
  onWindowScroll(): void {
    const threshold = 200; // px from bottom before triggering
    const position  = window.scrollY + window.innerHeight;
    const height    = document.documentElement.scrollHeight;
    if (position >= height - threshold) {
      this.loadMoreProducts();
    }
  }

  /* ─── Private ────────────────────────────────────────────────── */

  private initFetchPipeline(): void {
    this.fetchTrigger$
        .pipe(
            debounceTime(200),
            switchMap(() =>
                this.filterbaseService.loadFilterData(
                    this.filterbaseService.limit,
                    this.filterbaseService.offset
                )
            ),
            takeUntil(this.destroy$)
        )
        .subscribe({
          next: (data: any) => {
            this.loadingMask.setOnLoadingMask(false, false);
            this.productsList = data.object_list;
            this.totalPages   = data.page_size;
            this.isLoading    = false;
            this.productsChange.emit(this.productsList);
          },
          error: () => {
            this.loadingMask.setOnLoadingMask(false, false);
            this.isLoading = false;
          }
        });
  }

  private goToPage(page: number): void {
    this.loadingMask.setOnLoadingMask(true, true);
    this.filterbaseService.offset = page;
    this.fetchTrigger$.next();
  }


  private loadMoreProducts(): void {
    if (this.isLoadingMore || this.isLastPage) return;
    this.isLoadingMore = true;
    this.filterbaseService.offset++;
    this.filterbaseService
        .loadFilterData(this.filterbaseService.limit, this.filterbaseService.offset)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (data: any) => {
            this.productsList = [...this.productsList, ...data.object_list];
            this.isLoadingMore = false;
            this.productsChange.emit(this.productsList);
          },
          error: () => { this.isLoadingMore = false; }
        });
  }

  getProducts(mainCategory: string, subCategory: string | null = null): Observable<any> {
    return this.http
        .get(this.apiKey.getProducts(mainCategory, subCategory), this.httpOptions)
        .pipe(
            map(res => res ?? null),
            catchError(err => throwError(() => err.error ?? err))
        );
  }
}